import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  CircularProgress, Container, Alert, Paper, LinearProgress
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Play, Lock, AlertTriangle, Clock, Check, Shield, RefreshCw } from 'lucide-react';
import axios from 'axios';
import * as constants from 'src/utils/constants';

const API_BASE_URL = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1`;
const MEDIA_BASE_URL = `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}`;

// Session storage keys
const SESSION_TOKEN_KEY = 'video_session_token';
const VIDEO_TOKEN_KEY = 'video_token';
const SESSION_EXPIRY_KEY = 'video_session_expiry';

export default function SharedVideoPublic() {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading, pending_verification, verified, expired, removed, not_found, rate_limited, session_expired, error
  const [shareInfo, setShareInfo] = useState(null);
  const [videoData, setVideoData] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [accessCode, setAccessCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [error, setError] = useState('');
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(null);
  
  const videoRef = useRef(null);
  const tokenRefreshIntervalRef = useRef(null);
  const sessionTimerRef = useRef(null);

  // Get stored session token for this video
  const getStoredSession = useCallback(() => {
    const sessionToken = sessionStorage.getItem(`${SESSION_TOKEN_KEY}_${token}`);
    const videoToken = sessionStorage.getItem(`${VIDEO_TOKEN_KEY}_${token}`);
    const sessionExpiry = sessionStorage.getItem(`${SESSION_EXPIRY_KEY}_${token}`);
    
    if (sessionToken && sessionExpiry) {
      const expiryDate = new Date(sessionExpiry);
      if (expiryDate > new Date()) {
        return { sessionToken, videoToken, sessionExpiry: expiryDate };
      } else {
        // Clear expired session
        clearStoredSession();
      }
    }
    return null;
  }, [token]);

  // Store session tokens
  const storeSession = useCallback((sessionToken, videoToken, sessionExpiry) => {
    sessionStorage.setItem(`${SESSION_TOKEN_KEY}_${token}`, sessionToken);
    sessionStorage.setItem(`${VIDEO_TOKEN_KEY}_${token}`, videoToken);
    sessionStorage.setItem(`${SESSION_EXPIRY_KEY}_${token}`, sessionExpiry);
  }, [token]);

  // Clear stored session
  const clearStoredSession = useCallback(() => {
    sessionStorage.removeItem(`${SESSION_TOKEN_KEY}_${token}`);
    sessionStorage.removeItem(`${VIDEO_TOKEN_KEY}_${token}`);
    sessionStorage.removeItem(`${SESSION_EXPIRY_KEY}_${token}`);
  }, [token]);

  // Update video token in storage
  const updateVideoToken = useCallback((newVideoToken) => {
    sessionStorage.setItem(`${VIDEO_TOKEN_KEY}_${token}`, newVideoToken);
  }, [token]);

  // Fetch video URL using tokens
  const fetchVideoUrl = useCallback(async (sessionToken, videoToken) => {
    try {
      setIsLoadingVideo(true);
      const response = await axios.get(`${API_BASE_URL}/shared-video/stream/${token}/`, {
        headers: {
          'X-Video-Session-Token': sessionToken,
          'X-Video-Token': videoToken
        }
      });

      if (response.data?.success && response.data?.data?.video_url) {
        let url = response.data.data.video_url;
        // Normalize video URL
        if (url && !url.startsWith('http')) {
          if (url.includes('/api/v1/media/')) {
            url = url.replace('/api/v1/media/', '/media/');
          }
          url = `${MEDIA_BASE_URL}${url}`;
        }
        setVideoUrl(url);
        
        // Update video token with new one
        if (response.data.data.new_video_token) {
          updateVideoToken(response.data.data.new_video_token);
        }
        
        return true;
      }
    } catch (err) {
      console.error('Error fetching video URL:', err);
      const errorStatus = err.response?.data?.status;
      if (errorStatus === 'session_expired' || errorStatus === 'invalid_session') {
        clearStoredSession();
        setStatus('session_expired');
      } else if (errorStatus === 'invalid_video_token') {
        // Try to refresh token
        await refreshVideoToken(sessionToken);
      }
      return false;
    } finally {
      setIsLoadingVideo(false);
    }
  }, [token, updateVideoToken, clearStoredSession]);

  // Refresh video token
  const refreshVideoToken = useCallback(async (sessionToken) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/shared-video/refresh-token/${token}/`, {}, {
        headers: {
          'X-Video-Session-Token': sessionToken
        }
      });

      if (response.data?.success && response.data?.data?.video_token) {
        updateVideoToken(response.data.data.video_token);
        return response.data.data.video_token;
      }
    } catch (err) {
      console.error('Error refreshing video token:', err);
      if (err.response?.data?.status === 'session_expired') {
        clearStoredSession();
        setStatus('session_expired');
      }
    }
    return null;
  }, [token, updateVideoToken, clearStoredSession]);

  // Setup token refresh interval (refresh every 4 minutes to stay ahead of 5 min expiry)
  const setupTokenRefresh = useCallback((sessionToken) => {
    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current);
    }
    
    tokenRefreshIntervalRef.current = setInterval(async () => {
      const session = getStoredSession();
      if (session?.sessionToken) {
        await refreshVideoToken(session.sessionToken);
      }
    }, 4 * 60 * 1000); // Refresh every 4 minutes
  }, [getStoredSession, refreshVideoToken]);

  // Setup session countdown timer
  const setupSessionTimer = useCallback((sessionExpiry) => {
    if (sessionTimerRef.current) {
      clearInterval(sessionTimerRef.current);
    }
    
    const updateTimeLeft = () => {
      const now = new Date();
      const expiry = new Date(sessionExpiry);
      const diff = Math.max(0, Math.floor((expiry - now) / 1000));
      setSessionTimeLeft(diff);
      
      if (diff <= 0) {
        clearStoredSession();
        setStatus('session_expired');
      }
    };
    
    updateTimeLeft();
    sessionTimerRef.current = setInterval(updateTimeLeft, 1000);
  }, [clearStoredSession]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
      }
      if (sessionTimerRef.current) {
        clearInterval(sessionTimerRef.current);
      }
    };
  }, []);

  // Initial load - check for existing session
  useEffect(() => {
    const initializeVideo = async () => {
      const storedSession = getStoredSession();
      
      if (storedSession) {
        // Try to use existing session
        try {
          const response = await axios.get(`${API_BASE_URL}/shared-video/public/${token}/`, {
            headers: {
              'X-Video-Session-Token': storedSession.sessionToken
            }
          });
          
          if (response.data?.success && response.data?.status === 'verified') {
            setStatus('verified');
            setVideoData(response.data.data);
            
            // Update tokens if new ones provided
            if (response.data.data.video_token) {
              updateVideoToken(response.data.data.video_token);
            }
            
            // Fetch actual video URL
            await fetchVideoUrl(storedSession.sessionToken, response.data.data.video_token || storedSession.videoToken);
            
            // Setup token refresh and session timer
            setupTokenRefresh(storedSession.sessionToken);
            setupSessionTimer(response.data.data.session_expires_at || storedSession.sessionExpiry);
            return;
          }
        } catch (err) {
          // Session invalid, clear and continue to normal flow
          clearStoredSession();
        }
      }
      
      // No valid session, fetch share info
      fetchShareInfo();
    };
    
    initializeVideo();
  }, [token]);

  const fetchShareInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/shared-video/public/${token}/`);
      if (response.data?.success) {
        setStatus(response.data.status);
        setShareInfo(response.data.data);
        if (response.data.data?.remaining_attempts !== undefined) {
          setRemainingAttempts(response.data.data.remaining_attempts);
        }
      }
    } catch (err) {
      const errorStatus = err.response?.data?.status || 'error';
      setStatus(errorStatus);
      setError(err.response?.data?.message || 'An error occurred');
    }
  };

  const handleVerifyCode = async () => {
    if (!accessCode || accessCode.length !== 8) {
      setError('Please enter a valid 8-digit access code');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/shared-video/public/${token}/`, {
        access_code: accessCode
      });

      if (response.data?.success && response.data?.status === 'verified') {
        const data = response.data.data;
        
        // Store session tokens
        storeSession(data.session_token, data.video_token, data.session_expires_at);
        
        setStatus('verified');
        setVideoData(data);
        
        // Fetch actual video URL
        await fetchVideoUrl(data.session_token, data.video_token);
        
        // Setup token refresh and session timer
        setupTokenRefresh(data.session_token);
        setupSessionTimer(data.session_expires_at);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || 'Verification failed';
      setError(errorMsg);
      
      const errorStatus = err.response?.data?.status;
      if (errorStatus === 'expired') {
        setStatus('expired');
      } else if (errorStatus === 'rate_limited') {
        setStatus('rate_limited');
        setRemainingAttempts(0);
      } else if (err.response?.data?.remaining_attempts !== undefined) {
        setRemainingAttempts(err.response.data.remaining_attempts);
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 8);
    setAccessCode(value);
    setError('');
  };

  // Prevent right-click on video
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  // Format time for display
  const formatTimeLeft = (seconds) => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Loading state
  if (status === 'loading') {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#f5f5f5'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // Expired state
  if (status === 'expired') {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#f5f5f5'
      }}>
        <Container maxWidth="sm">
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <Clock size={64} color="#f44336" style={{ marginBottom: 16 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#f44336' }}>
              Access Expired
            </Typography>
            <Typography variant="body1" color="text.secondary">
              This video share has expired and is no longer accessible.
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 2 }}>
              Please contact the person who shared this video with you for a new link.
            </Typography>
          </Card>
        </Container>
      </Box>
    );
  }

  // Removed state
  if (status === 'removed') {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#f5f5f5'
      }}>
        <Container maxWidth="sm">
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <AlertTriangle size={64} color="#ff9800" style={{ marginBottom: 16 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#ff9800' }}>
              Share Removed
            </Typography>
            <Typography variant="body1" color="text.secondary">
              This video share has been removed and is no longer accessible.
            </Typography>
          </Card>
        </Container>
      </Box>
    );
  }

  // Not found state
  if (status === 'not_found') {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#f5f5f5'
      }}>
        <Container maxWidth="sm">
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <AlertTriangle size={64} color="#9e9e9e" style={{ marginBottom: 16 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Invalid Link
            </Typography>
            <Typography variant="body1" color="text.secondary">
              This share link is invalid or does not exist.
            </Typography>
          </Card>
        </Container>
      </Box>
    );
  }

  // Rate limited state
  if (status === 'rate_limited') {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#f5f5f5'
      }}>
        <Container maxWidth="sm">
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <Shield size={64} color="#f44336" style={{ marginBottom: 16 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#f44336' }}>
              Too Many Attempts
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You have exceeded the maximum number of access code attempts.
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mt: 2 }}>
              Please wait 30 minutes before trying again, or contact the person who shared this video.
            </Typography>
          </Card>
        </Container>
      </Box>
    );
  }

  // Session expired state
  if (status === 'session_expired') {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#f5f5f5'
      }}>
        <Container maxWidth="sm">
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <RefreshCw size={64} color="#ff9800" style={{ marginBottom: 16 }} />
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#ff9800' }}>
              Session Expired
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Your viewing session has expired. Please enter the access code again to continue watching.
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                setStatus('pending_verification');
                setAccessCode('');
                setError('');
                fetchShareInfo();
              }}
              sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}
            >
              Enter Code Again
            </Button>
          </Card>
        </Container>
      </Box>
    );
  }

  // Verified state - show video with security features
  if (status === 'verified' && (videoUrl || isLoadingVideo)) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#1a1a1a',
        py: 4
      }}>
        <Container maxWidth="lg">
          {/* Header with session info */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
              {videoData?.file_name || 'Shared Video'}
            </Typography>
            {videoData?.company_name && (
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Shared by {videoData.company_name}
              </Typography>
            )}
            
            {/* Session timer */}
            {sessionTimeLeft !== null && (
              <Box sx={{ 
                mt: 2, 
                display: 'inline-flex', 
                alignItems: 'center', 
                gap: 1,
                bgcolor: sessionTimeLeft < 300 ? 'rgba(244,67,54,0.2)' : 'rgba(255,255,255,0.1)',
                px: 2, 
                py: 0.5, 
                borderRadius: 2 
              }}>
                <Clock size={16} color={sessionTimeLeft < 300 ? '#f44336' : '#fff'} />
                <Typography variant="body2" sx={{ 
                  color: sessionTimeLeft < 300 ? '#f44336' : 'rgba(255,255,255,0.7)',
                  fontWeight: 500
                }}>
                  Session: {formatTimeLeft(sessionTimeLeft)}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Video Player with download prevention */}
          <Paper 
            elevation={8}
            sx={{ 
              bgcolor: '#000', 
              borderRadius: 2,
              overflow: 'hidden',
              mb: 3,
              position: 'relative'
            }}
            onContextMenu={handleContextMenu}
          >
            {isLoadingVideo ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '400px'
              }}>
                <CircularProgress sx={{ color: 'white' }} />
              </Box>
            ) : videoUrl ? (
              <video
                ref={videoRef}
                controls
                autoPlay
                controlsList="nodownload noremoteplayback"
                disablePictureInPicture
                onContextMenu={handleContextMenu}
                style={{ 
                  width: '100%', 
                  maxHeight: '70vh',
                  display: 'block'
                }}
                src={videoUrl}
              >
                Your browser does not support the video tag.
              </video>
            ) : (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '400px',
                flexDirection: 'column'
              }}>
                <AlertTriangle size={48} color="#ff9800" />
                <Typography sx={{ color: 'white', mt: 2 }}>
                  Unable to load video
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Security notice and expiry info */}
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: 1,
              bgcolor: 'rgba(255,255,255,0.1)',
              px: 2, 
              py: 1, 
              borderRadius: 2,
              mb: 2
            }}>
              <Shield size={16} color="#4caf50" />
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                This video is protected and monitored
              </Typography>
            </Box>
            
            {videoData?.expires_at && (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                Share expires: {new Date(videoData.expires_at).toLocaleString()}
              </Typography>
            )}
          </Box>
        </Container>
      </Box>
    );
  }

  // Pending verification - show code input
  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <Container maxWidth="sm">
        <Card sx={{ p: 4, borderRadius: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ 
              display: 'inline-flex', 
              p: 2, 
              borderRadius: '50%', 
              bgcolor: '#e8f5e9',
              mb: 2
            }}>
              <Lock size={40} color="#2e7d32" />
            </Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Protected Video
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Enter the 8-digit access code to view this video
            </Typography>
          </Box>

          {shareInfo && (
            <Box sx={{ 
              mb: 3, 
              p: 2, 
              bgcolor: '#f5f5f5', 
              borderRadius: 2,
              textAlign: 'center'
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {shareInfo.file_name || 'Video'}
              </Typography>
              {shareInfo.company_name && (
                <Typography variant="body2" color="text.secondary">
                  From: {shareInfo.company_name}
                </Typography>
              )}
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {/* Show remaining attempts warning */}
          {remainingAttempts !== null && remainingAttempts <= 3 && remainingAttempts > 0 && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              {remainingAttempts} attempt{remainingAttempts !== 1 ? 's' : ''} remaining before temporary lockout
            </Alert>
          )}

          <TextField
            fullWidth
            label="Access Code"
            placeholder="Enter 8-digit code"
            value={accessCode}
            onChange={handleCodeChange}
            disabled={remainingAttempts === 0}
            inputProps={{ 
              maxLength: 8,
              style: { 
                textAlign: 'center', 
                fontSize: '1.5rem', 
                letterSpacing: '0.5rem',
                fontWeight: 600
              }
            }}
            sx={{ mb: 3 }}
            autoFocus
          />

          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            onClick={handleVerifyCode}
            loading={isVerifying}
            disabled={remainingAttempts === 0}
            startIcon={<Check size={20} />}
            sx={{ 
              py: 1.5,
              bgcolor: '#2e7d32',
              '&:hover': { bgcolor: '#1b5e20' }
            }}
          >
            Verify & Watch
          </LoadingButton>

          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ display: 'block', textAlign: 'center', mt: 2 }}
          >
            The access code was sent to you via email
          </Typography>
          
          {/* Security notice */}
          <Box sx={{ 
            mt: 3, 
            pt: 2, 
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}>
            <Shield size={14} color="#9e9e9e" />
            <Typography variant="caption" color="text.disabled">
              Protected by secure access control
            </Typography>
          </Box>
        </Card>
      </Container>
    </Box>
  );
}
